import { RefreshCw, Search, Filter, Lock } from "lucide-react";

export function Section({ title, subtitle, onRefresh, loading, toolbar, children }) {
  return (
    <div className="bg-white border border-black/10 rounded-sm">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 px-5 py-4 border-b border-black/10">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#0a0a0a]" dangerouslySetInnerHTML={{ __html: title }} />
          {subtitle && <p className="mt-1 text-xs text-[#4a4a4a]" dangerouslySetInnerHTML={{ __html: subtitle }} />}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {toolbar}
          {onRefresh && (
            <button onClick={onRefresh} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

export function Empty({ msg }) {
  return <div className="px-5 py-12 text-center text-sm text-[#4a4a4a]">{msg}</div>;
}

export function Pill({ cls, label }) {
  return <span className={`inline-block whitespace-nowrap px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${cls}`}>{label}</span>;
}

export function KpiGrid({ cards, cols = "grid-cols-2 md:grid-cols-4" }) {
  if (!cards.length) return null;
  return (
    <div className={`grid ${cols} gap-px bg-black/10 border border-black/10 mb-6`}>
      {cards.map((c, i) => (
        <div key={i} className="bg-white p-5 relative overflow-hidden" data-testid={`client-kpi-${i}`}>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
            {c.icon} {c.label}
          </div>
          <div className={`mt-2 text-2xl md:text-3xl font-bold ${c.accent || "text-[#0a0a0a]"}`}>{c.value}</div>
          {c.tone && <span className={`absolute top-0 left-0 h-1 w-full ${c.tone}`} />}
        </div>
      ))}
    </div>
  );
}

export function TabButton({ active, onClick, icon, label, locked, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] border-b-2 transition-colors whitespace-nowrap ${
        active ? "border-[#ff5722] text-[#ff5722] font-bold" : "border-transparent text-[#4a4a4a] hover:text-[#0a0a0a]"
      }`}>
      {icon} {label} {locked && <Lock className="h-3 w-3 text-[#ff5722]" />}
    </button>
  );
}

export function SelectPill({ icon, prefix, value, onChange, options, fmt, testid }) {
  return (
    <div className="relative inline-flex items-center">
      <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">{icon}</div>
      <span className="absolute left-7 top-1/2 -translate-y-1/2 text-[10px] font-mono uppercase tracking-[0.15em] text-[#9a9a9a] pointer-events-none">{prefix}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="font-mono text-xs border border-black/15 rounded-full pl-[88px] pr-7 py-1.5 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20 appearance-none bg-white"
      >
        {options.map((o) => (
          <option key={o} value={o}>{fmt(o)}</option>
        ))}
      </select>
    </div>
  );
}

export function FilterBar({ q, onQ, value, onValue, options, labelFn, placeholder }) {
  const label = labelFn || ((v) => v === "all" ? "All" : v.replace("_", " "));
  return (
    <div className="flex items-center gap-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
        <input
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder={placeholder || "Search…"}
          data-testid="client-filter-search"
          className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-3 py-1.5 w-56 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20"
        />
      </div>
      <div className="relative">
        <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a] pointer-events-none" />
        <select
          value={value}
          onChange={(e) => onValue(e.target.value)}
          data-testid="client-filter-select"
          className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-7 py-1.5 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20 appearance-none bg-white capitalize"
        >
          {options.map((o) => (
            <option key={o} value={o} className="capitalize">{label(o)}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function ProjectsFilterBar({ q, onQ, webStatus, onWebStatus, webOptions, worktype, onWorktype }) {
  const fmt = (v) => v === "all" ? "All" : v;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
        <input
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder="Search projects, latest update, URL…"
          data-testid="client-filter-search"
          className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-3 py-1.5 w-56 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20"
        />
      </div>
      <SelectPill icon={<Filter className="h-3.5 w-3.5 text-[#9a9a9a]" />}
        prefix="Website" value={webStatus} onChange={onWebStatus} options={webOptions} fmt={fmt}
        testid="client-filter-website-status" />
      {onWorktype && (
        <SelectPill icon={<Filter className="h-3.5 w-3.5 text-[#9a9a9a]" />}
          prefix="Worktype" value={worktype} onChange={onWorktype} options={["all", "Build", "Enhance"]} fmt={fmt}
          testid="client-filter-worktype" />
      )}
    </div>
  );
}
